import * as React from 'react';
import { observer } from 'mobx-react';
import { classes, keyframes, style } from 'typestyle';
import { PillStyle, PillStyleDefault, PillStyleDisabled } from './pill';
import {
  colorBackground,
  colorBrand,
  colorContrast,
  colorError,
  colorSlate,
  colorSubtle,
  colorWhite,
  colorDarkBlueGrey,
  colorBlueGrey,
} from '../theme/color';
import { resetAnchorStyles } from '../theme/reset';
import { ColorHelper, important, px, em, percent } from 'csx';
import { animationBounce } from '../theme/animation';
import { Style } from 'react-motion';

export const buttonPillHelpers = {
  customColor: (backgroundColor: ColorHelper, textColor: ColorHelper = colorWhite, fixTextColor = false) => ({
    ...PillStyle,
    ...PillStyleDefault,
    color: fixTextColor ? important(textColor.toString()) : textColor.toString(),
    background: backgroundColor.toString(),
    $nest: {
      '&:hover': {
        backgroundColor: backgroundColor.darken(0.15).toString(),
      },
      '&&:focus': {
        backgroundColor: backgroundColor.darken(0.15).toString(),
        outline: 'none',
      },
      '&&&[disabled]': {
        ...PillStyleDisabled,
        '&:focus': {
          backgroundColor: colorBrand.darken(0.15).toString(),
          outline: 'none',
        },
      },
    },
  }),
  customColorLink: (textColor: ColorHelper = colorWhite) => ({
    textDecoration: important('none'),
    $nest: {
      ...resetAnchorStyles(textColor.toString(), true),
    },
  }),
  bouncing: { ...animationBounce },
};

export interface ButtonPillProps {
  primary?: boolean;
  disabled?: boolean;
  onClick?(event: React.MouseEvent<any>): void;
  target?: string;
  className?: string;
  muted?: boolean;
  unstyled?: boolean;
  type?: string;
  href?: string;
  bouncing?: boolean;
  cursor?: string;
  darken?: boolean;
  xsmall?: boolean;
  tertiary?: boolean;
  xlarge?: boolean;
  icon?: boolean;
}

@observer
export class ButtonPill extends React.Component<ButtonPillProps> {
  render() {
    const {
      primary = false,
      disabled = false,
      onClick,
      className,
      children,
      type,
      href,
      muted,
      unstyled = false,
      target,
      bouncing,
      cursor,
      darken,
      xsmall,
      tertiary,
      xlarge,
      icon,
    } = this.props;

    const props = {
      type: href ? void 0 : type || 'button',
      children,
      onClick,
      className: classes(
        !unstyled &&
          (primary ? ButtonPill.styles.primary : muted ? ButtonPill.styles.muted : ButtonPill.styles.standard),
        !unstyled &&
          href &&
          (primary
            ? ButtonPill.styles.primaryLink
            : muted
            ? ButtonPill.styles.mutedLink
            : ButtonPill.styles.standardLink),
        bouncing && ButtonPill.styles.bouncing,
        className,
        cursor != undefined ? style({ cursor: cursor }) : null,
        darken === true ? style({ backgroundColor: colorBackground.darken(0.15).toString() }) : null,
        disabled ? style({ ...PillStyleDisabled }) : null,
        xsmall ? style({ fontSize: px(12), lineHeight: px(20), padding: `${px(2)} ${px(8)}` }) : null,
        tertiary
          ? style({
              background: `${colorBackground.toString()}`,
              boxShadow: `0 ${px(4)} ${px(6)} ${px(1)} rgba(0,0,0,0.2)`,
              color: important(colorSlate.toString()),
              cursor: 'pointer',
              $nest: {
                '&:hover': {
                  background: colorDarkBlueGrey.toString(),
                },
                '&:focus': {
                  backgroundColor: important(colorBlueGrey.toString()),
                },
              },
            })
          : null,
        xlarge ? style({ borderRadius: em(1.6), padding: `${em(0.6)} ${em(1.4)}` }) : null,
        icon
          ? style({
              width: px(32),
              padding: px(0),
              borderRadius: percent(100),
              height: px(32),
              $nest: {
                img: {
                  height: px(16),
                },
              },
            })
          : null,
      ),
    };

    return href ? (
      <a {...props} href={href} target={target || '_self'} />
    ) : type === 'submit' ? (
      <button {...props} disabled={disabled} />
    ) : (
      <div role="button" {...props} aria-disabled={disabled} />
    );
  }

  static styles = {
    primary: style(buttonPillHelpers.customColor(colorBrand, colorWhite)),
    primaryLink: style(buttonPillHelpers.customColorLink(colorWhite)),
    standard: style(buttonPillHelpers.customColor(colorContrast, colorWhite)),
    standardLink: style(buttonPillHelpers.customColorLink(colorWhite)),
    muted: style(buttonPillHelpers.customColor(colorBackground, colorBrand)),
    mutedLink: style(buttonPillHelpers.customColorLink(colorBrand)),
    bouncing: style(buttonPillHelpers.bouncing),
    danger: style(buttonPillHelpers.customColor(colorError, colorWhite)),
  };
}
